import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ContactPicker } from '@/components/ContactPicker';
import { DeviceContactButton } from '@/components/DeviceContactButton';
import { TERMS_OF_SERVICE_VERSION } from '@/constants/legal';
import { extractApiErrorMessage } from '@/lib/api-error';
import { KE_PHONE_PATTERN, normalizeKePhoneToE164 } from '@/lib/phone';
import { createInvitation, recordConsent } from '@/services/api.client';
import type { Invitation } from '@/types';

export type InviteScope = 'resident' | 'enterprise' | 'employee';

const coreSchema = z.object({
  visitorName: z.string().min(1, 'Visitor name is required'),
  visitorPhone: z
    .string()
    .min(9, 'Enter a valid phone number')
    .regex(KE_PHONE_PATTERN, 'Enter a valid Kenyan phone number (e.g. +254712345678)'),
  visitDate: z.string().min(1, "Pick the day they're coming"),
  visitorEmail: z.string().email('Enter a valid email').or(z.literal('')).optional(),
  notes: z.string().max(200, 'Max 200 characters').optional(),
  partySize: z.number().int().min(1).optional(),
  meetingLatitude: z.number().min(-90).max(90).optional(),
  meetingLongitude: z.number().min(-180).max(180).optional(),
  meetingLabel: z.string().max(100, 'Max 100 characters').optional(),
  // KDPA / ToS §6 — the inviting user attests they have a lawful basis
  // to share the visitor's contact details with the Platform. Required.
  lawfulBasisAttested: z.boolean().refine((v) => v === true, {
    message: 'Please confirm you have asked the visitor for permission',
  }),
});

type FormValues = z.infer<typeof coreSchema>;

function isoDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function computeValidity(dateString: string): { validFrom: string; validUntil: string } {
  const parts = dateString.split('-').map((s) => Number(s));
  const y = parts[0] ?? new Date().getFullYear();
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  const start = new Date(y, m - 1, d, 0, 0, 0, 0);
  const end = new Date(y, m - 1, d, 23, 59, 59, 999);
  const now = new Date();
  // If the chosen day is today, start from "now" so they can use the pass
  // immediately; otherwise start at midnight on the chosen day.
  const validFrom = start < now && end > now ? now : start;
  return { validFrom: validFrom.toISOString(), validUntil: end.toISOString() };
}

interface Props {
  propertyId: string;
  unitId?: string;
  scope: InviteScope;
  onSuccess: (invitation: Invitation) => void;
  onCancel?: () => void;
  submitLabel?: string;
}

export function InviteVisitorForm({
  propertyId,
  unitId,
  scope,
  onSuccess,
  onCancel,
  submitLabel = 'Send Invitation',
}: Props) {
  const [apiError, setApiError] = useState<string | null>(null);
  const [showMore, setShowMore] = useState(false);
  const showExtended = scope === 'resident';
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(coreSchema),
    defaultValues: {
      lawfulBasisAttested: false as unknown as true,
    },
  });

  const lawfulBasisAttested = watch('lawfulBasisAttested');

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const { validFrom, validUntil } = computeValidity(values.visitDate);
      return createInvitation(propertyId, {
        visitorName: values.visitorName,
        // Schema already validated the shape; normalisation cannot return null here.
        visitorPhone: normalizeKePhoneToE164(values.visitorPhone) ?? values.visitorPhone,
        validFrom,
        validUntil,
        ...(values.visitorEmail ? { visitorEmail: values.visitorEmail } : {}),
        ...(values.notes ? { note: values.notes } : {}),
        ...(values.partySize && values.partySize > 1 && !isNaN(values.partySize)
          ? { partySize: values.partySize }
          : {}),
        ...(values.meetingLatitude !== undefined && values.meetingLongitude !== undefined
          ? { meetingLatitude: values.meetingLatitude, meetingLongitude: values.meetingLongitude }
          : {}),
        ...(values.meetingLabel ? { meetingLabel: values.meetingLabel } : {}),
      });
    },
    onSuccess: (inv) => {
      setApiError(null);
      // Invalidate the active invitations list so the new invitation appears
      // immediately when the resident navigates to /resident/visitors.
      void queryClient.invalidateQueries({ queryKey: ['invitations', propertyId] });
      // Record the lawful-basis attestation as a consent event. Fire-and-
      // forget — a failure here is logged but does not roll back the
      // invitation. The attestation lives in the form audit trail too.
      void recordConsent({
        scope: 'visitor_data_lawful_basis',
        version: TERMS_OF_SERVICE_VERSION,
        granted: true,
        propertyId,
      }).catch((err) => {
        console.warn('Failed to record visitor-data lawful-basis attestation', err);
      });
      onSuccess(inv);
    },
    onError: (err) => setApiError(extractApiErrorMessage(err, 'Failed to send invitation')),
  });

  const today = isoDate(new Date());
  const tomorrow = isoDate(new Date(Date.now() + 86_400_000));

  return (
    <form onSubmit={(e) => void handleSubmit((v) => mutation.mutate(v))(e)} noValidate>
      <FieldGroup>
        {showExtended && unitId && (
          <ContactPicker
            propertyId={propertyId}
            unitId={unitId}
            onSelect={(contact) => {
              setValue('visitorName', contact.name);
              setValue('visitorPhone', contact.phone);
            }}
          />
        )}

        <DeviceContactButton
          onPick={(contact) => {
            if (contact.name) setValue('visitorName', contact.name, { shouldValidate: true });
            if (contact.phone) setValue('visitorPhone', contact.phone, { shouldValidate: true });
          }}
        />

        {/* ── Essentials ─────────────────────────────────────────── */}
        <Field>
          <FieldLabel htmlFor="visitorName">Visitor Name *</FieldLabel>
          <Input
            id="visitorName"
            placeholder="Jane Kamau"
            aria-invalid={!!errors.visitorName}
            {...register('visitorName')}
          />
          {errors.visitorName && (
            <p className="text-sm text-destructive">{errors.visitorName.message}</p>
          )}
        </Field>

        <Field>
          <FieldLabel htmlFor="visitorPhone">Phone *</FieldLabel>
          <Input
            id="visitorPhone"
            type="tel"
            placeholder="+254 700 000 000"
            aria-invalid={!!errors.visitorPhone}
            {...register('visitorPhone')}
          />
          {errors.visitorPhone && (
            <p className="text-sm text-destructive">{errors.visitorPhone.message}</p>
          )}
        </Field>

        <Field>
          <FieldLabel htmlFor="visitDate">When are they coming? *</FieldLabel>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setValue('visitDate', today, { shouldValidate: true })}
              className="rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => setValue('visitDate', tomorrow, { shouldValidate: true })}
              className="rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
            >
              Tomorrow
            </button>
          </div>
          <Input
            id="visitDate"
            type="date"
            min={today}
            aria-invalid={!!errors.visitDate}
            {...register('visitDate')}
          />
          {errors.visitDate && (
            <p className="text-sm text-destructive">{errors.visitDate.message}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Pass is valid for the whole day they're visiting.
          </p>
        </Field>

        {/* ── Meeting point (always visible — share location with visitor) ─── */}
        {showExtended && (
          <div className="space-y-2 rounded-md border border-input p-3">
            <p className="text-sm font-medium">
              Share meeting point{' '}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </p>
            <p className="text-xs text-muted-foreground">
              Help your visitor find you — they&apos;ll get a &quot;Get Directions&quot; button on
              their invite.
            </p>

            {watch('meetingLatitude') !== undefined && watch('meetingLongitude') !== undefined ? (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  📍 Location set: {watch('meetingLatitude')?.toFixed(5)},{' '}
                  {watch('meetingLongitude')?.toFixed(5)}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setValue('meetingLatitude', undefined);
                    setValue('meetingLongitude', undefined);
                  }}
                  className="text-xs text-destructive hover:underline"
                >
                  Clear location
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (!navigator.geolocation) {
                    setApiError('Location is not available. Make sure you are using HTTPS.');
                    return;
                  }
                  navigator.geolocation.getCurrentPosition(
                    (pos) => {
                      setValue('meetingLatitude', pos.coords.latitude, {
                        shouldValidate: true,
                      });
                      setValue('meetingLongitude', pos.coords.longitude, {
                        shouldValidate: true,
                      });
                    },
                    (err) => {
                      if (err.code === err.PERMISSION_DENIED) {
                        setApiError(
                          'Location permission was denied. Please enable location access in your browser settings and reload the page.',
                        );
                      } else if (err.code === err.POSITION_UNAVAILABLE) {
                        setApiError(
                          'Could not determine your location. Make sure location services are enabled on your device.',
                        );
                      } else {
                        setApiError('Location request timed out. Please try again.');
                      }
                    },
                    { enableHighAccuracy: false, timeout: 10000 },
                  );
                }}
                className="rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
              >
                📍 Use my current location
              </button>
            )}

            <Field>
              <FieldLabel htmlFor="meetingLabel">
                Label <span className="text-muted-foreground font-normal">(optional)</span>
              </FieldLabel>
              <Input
                id="meetingLabel"
                placeholder="e.g. House 14, Clubhouse"
                maxLength={100}
                {...register('meetingLabel')}
              />
            </Field>
          </div>
        )}

        {/* ── Optional extras (collapsed by default) ─────────────── */}
        {showExtended && (
          <>
            <button
              type="button"
              onClick={() => setShowMore(!showMore)}
              className="flex items-center gap-1.5 text-sm text-[var(--text-accent)] hover:underline mt-1"
            >
              <ChevronDown
                className={`size-4 transition-transform ${showMore ? 'rotate-180' : ''}`}
              />
              {showMore ? 'Hide options' : 'More options'}
            </button>

            {showMore && (
              <>
                <Field>
                  <FieldLabel htmlFor="visitorEmail">
                    Email <span className="text-muted-foreground font-normal">(optional)</span>
                  </FieldLabel>
                  <Input
                    id="visitorEmail"
                    type="email"
                    placeholder="jane@example.com"
                    {...register('visitorEmail')}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="notes">
                    Notes <span className="text-muted-foreground font-normal">(optional)</span>
                  </FieldLabel>
                  <Textarea
                    id="notes"
                    placeholder="Reason for visit, instructions, etc."
                    maxLength={200}
                    {...register('notes')}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="partySize">
                    Number of people{' '}
                    <span className="text-muted-foreground font-normal">(optional)</span>
                  </FieldLabel>
                  <Input
                    id="partySize"
                    type="number"
                    min={1}
                    step={1}
                    placeholder="1"
                    {...register('partySize', { valueAsNumber: true })}
                  />
                  <p className="text-xs text-muted-foreground">
                    How many people will arrive with this invite? Defaults to 1.
                  </p>
                </Field>
              </>
            )}
          </>
        )}

        {/* ── Lawful basis attestation (ToS §6) ─────────────────────── */}
        <Field>
          <label className="flex items-start gap-3 text-sm">
            <Checkbox
              id="lawfulBasisAttested"
              checked={!!lawfulBasisAttested}
              onCheckedChange={(v) =>
                setValue(
                  'lawfulBasisAttested',
                  v === true ? (true as true) : (false as unknown as true),
                  { shouldValidate: true },
                )
              }
              aria-invalid={!!errors.lawfulBasisAttested}
            />
            <span>
              I confirm that I am authorised to share this visitor&apos;s details for this visit.
            </span>
          </label>
          {errors.lawfulBasisAttested && (
            <p className="text-xs text-destructive">
              {errors.lawfulBasisAttested.message as string}
            </p>
          )}
        </Field>

        {apiError && (
          <p className="text-sm text-destructive text-center" role="alert">
            {apiError}
          </p>
        )}

        <div className="flex gap-3 justify-end">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={mutation.isPending || !lawfulBasisAttested}>
            {mutation.isPending ? 'Sending…' : submitLabel}
          </Button>
        </div>
      </FieldGroup>
    </form>
  );
}
